/**
 * Provider-safe public copies of operator-approved marketplace images.
 * Private product-media originals stay private. Publication copies live in a
 * dedicated public bucket under an opaque asset identity — no signed tokens.
 */

export const PROVIDER_PUBLICATION_MEDIA_BUCKET = "publication-media";
export const PRIVATE_PRODUCT_MEDIA_BUCKET = "product-media";
export const MAX_PROVIDER_IMAGE_URL_CHARS = 500;
export const MAX_PROVIDER_IMAGE_URLS_COMBINED_CHARS = 3975;

export type MediaDeny = { ok: false; code: string; message: string };

export interface PublicationMediaRow {
  media_asset_id: string;
  storage_key: string | null;
  mime_type: string | null;
  original_filename: string | null;
  is_primary: boolean;
  position: number;
}

const SAFE_EXT: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
};

export function mimeToSafeExt(mime: string | null | undefined, filename?: string | null): string | null {
  const fromMime = SAFE_EXT[String(mime || "").trim().toLowerCase()];
  if (fromMime) return fromMime;
  const ext = String(filename || "").split(".").pop()?.toLowerCase();
  if (ext === "jpeg" || ext === "jpg") return "jpg";
  if (ext === "png" || ext === "webp") return ext;
  return null;
}

export function publicationMediaObjectKey(opts: { mediaAssetId: string; mimeType?: string | null; filename?: string | null }): string | null {
  const id = String(opts.mediaAssetId || "").replace(/[^a-fA-F0-9-]/g, "");
  if (!/^[0-9a-fA-F-]{32,36}$/.test(id)) return null;
  const ext = mimeToSafeExt(opts.mimeType, opts.filename);
  if (!ext) return null;
  return `${id.replace(/-/g, "").toLowerCase()}.${ext}`;
}

export function publicationMediaPublicUrl(opts: { supabaseUrl: string; objectKey: string }): string {
  const origin = String(opts.supabaseUrl || "").replace(/\/+$/, "");
  return `${origin}/storage/v1/object/public/${PROVIDER_PUBLICATION_MEDIA_BUCKET}/${opts.objectKey}`;
}

export function isExpiringSignedUrl(url: string): boolean {
  const u = String(url || "");
  if (!u.startsWith("https://")) return false;
  let parsed: URL;
  try {
    parsed = new URL(u);
  } catch {
    return true;
  }
  const q = parsed.searchParams;
  return Boolean(
    q.get("token")
    || q.get("X-Amz-Signature")
    || q.get("Signature")
    || q.get("Expires")
    || q.get("expires")
    || parsed.pathname.includes("/object/sign/"),
  );
}

export function isPrivateProductMediaUrl(url: string): boolean {
  const u = String(url || "");
  return u.includes(`/object/sign/${PRIVATE_PRODUCT_MEDIA_BUCKET}/`)
    || u.includes(`/object/authenticated/${PRIVATE_PRODUCT_MEDIA_BUCKET}/`);
}

export function assertProviderPublicImageUrl(url: string): { ok: true } | MediaDeny {
  if (typeof url !== "string" || !url.startsWith("https://")) {
    return { ok: false, code: "PROVIDER_IMAGE_NOT_HTTPS", message: "Provider image URL must be HTTPS." };
  }
  if (url.length > MAX_PROVIDER_IMAGE_URL_CHARS) {
    return { ok: false, code: "PROVIDER_IMAGE_URL_TOO_LONG", message: `Provider image URL exceeds ${MAX_PROVIDER_IMAGE_URL_CHARS} characters.` };
  }
  if (isExpiringSignedUrl(url) || isPrivateProductMediaUrl(url)) {
    return { ok: false, code: "PROVIDER_IMAGE_SIGNED_URL_FORBIDDEN", message: "Expiring or private signed media URLs cannot be sent to marketplaces." };
  }
  if (!url.includes(`/object/public/${PROVIDER_PUBLICATION_MEDIA_BUCKET}/`)) {
    return { ok: false, code: "PROVIDER_IMAGE_BUCKET_MISMATCH", message: "Provider image must come from the publication-media public bucket." };
  }
  return { ok: true };
}

export function assertProviderPublicationImageSet(urls: string[]): { ok: true; combinedChars: number } | MediaDeny {
  if (!Array.isArray(urls) || urls.length === 0) {
    return { ok: false, code: "PROVIDER_IMAGE_REQUIRED", message: "At least one provider-public image URL is required." };
  }
  let combined = 0;
  for (const url of urls) {
    const one = assertProviderPublicImageUrl(url);
    if (one.ok === false) return one;
    combined += url.length;
  }
  if (combined > MAX_PROVIDER_IMAGE_URLS_COMBINED_CHARS) {
    return { ok: false, code: "PROVIDER_IMAGE_URLS_COMBINED_TOO_LONG", message: `Combined provider image URLs exceed ${MAX_PROVIDER_IMAGE_URLS_COMBINED_CHARS} characters.` };
  }
  return { ok: true, combinedChars: combined };
}

export function uniqueMediaAssetsForPublication(media: unknown): PublicationMediaRow[] {
  const rows = Array.isArray(media) ? media : [];
  const scored: PublicationMediaRow[] = [];
  for (const item of rows) {
    if (!item || typeof item !== "object") continue;
    const rec = item as Record<string, unknown>;
    const id = String(rec.media_asset_id || rec.id || "").trim();
    if (!id) continue;
    scored.push({
      media_asset_id: id,
      storage_key: typeof rec.storage_key === "string" ? rec.storage_key : null,
      mime_type: typeof rec.mime_type === "string" ? rec.mime_type : null,
      original_filename: typeof rec.original_filename === "string" ? rec.original_filename : null,
      is_primary: rec.is_primary === true,
      position: Number(rec.position ?? 0) || 0,
    });
  }
  scored.sort((a, b) => Number(b.is_primary) - Number(a.is_primary) || a.position - b.position);
  const seen = new Set<string>();
  const unique: PublicationMediaRow[] = [];
  for (const row of scored) {
    if (seen.has(row.media_asset_id)) continue;
    seen.add(row.media_asset_id);
    unique.push(row);
  }
  return unique;
}

export function decideEbayPartialRemoteState(opts: {
  expectedSkus: string[];
  inventory: Record<string, { status: number }>;
  offers: Record<string, { status: number; offerCount: number; listingIds: string[] }>;
  groupStatus: number;
}): { ok: true; mode: "create" | "recover_unpublished" } | MediaDeny {
  const skus = opts.expectedSkus.filter(Boolean);
  if (skus.length === 0) {
    return { ok: false, code: "REMOTE_REVIEW", message: "Expected SKUs are required for remote preflight." };
  }
  const listingIds = new Set<string>();
  let inv200 = 0;
  let inv404 = 0;
  let offersExactOne = 0;
  let offersEmpty = 0;
  for (const sku of skus) {
    const inv = opts.inventory[sku];
    if (!inv || (inv.status !== 200 && inv.status !== 404)) {
      return { ok: false, code: "REMOTE_REVIEW", message: `Inventory GET for ${sku} was ambiguous.` };
    }
    if (inv.status === 200) inv200 += 1;
    else inv404 += 1;
    const off = opts.offers[sku];
    const count = off?.offerCount ?? 0;
    const ids = [...new Set((off?.listingIds ?? []).filter(Boolean))];
    ids.forEach((id) => listingIds.add(id));
    if (count === 1) offersExactOne += 1;
    else if (count === 0) offersEmpty += 1;
    else {
      return { ok: false, code: "REMOTE_REVIEW", message: `Unexpected offer count for ${sku}.` };
    }
  }
  if (listingIds.size > 0) {
    return { ok: false, code: "LIVE_LISTING_EXISTS", message: "A live listing ID already exists; remote state requires review." };
  }
  const group200 = opts.groupStatus === 200;
  const group404 = opts.groupStatus === 404;
  if (inv404 === skus.length && offersEmpty === skus.length && group404) {
    return { ok: true, mode: "create" };
  }
  if (inv200 === skus.length && offersExactOne === skus.length && group200) {
    return { ok: true, mode: "recover_unpublished" };
  }
  return { ok: false, code: "REMOTE_REVIEW", message: "Remote inventory/offer/group state is mixed or incomplete." };
}
